/**
 * `supabase.functions.invoke` may hand us the response body as a string, an
 * ArrayBuffer, a Blob, or a JSON object with `{ data }` / `{ text }` fields.
 * QZ Tray's signing hooks require **plain text** (PEM or base64 signature),
 * so we always coerce to a trimmed string here.
 */
export async function coerceQzPlainTextResponse(data: unknown): Promise<string> {
  if (data === null || data === undefined) return "";

  if (typeof data === "string") return data.trim();

  // ArrayBuffer / typed arrays. `instanceof ArrayBuffer` can be unreliable
  // across realms (jsdom, workers), so also match by constructor name.
  const ctorName = (data as { constructor?: { name?: string } })?.constructor?.name;
  if (
    (typeof ArrayBuffer !== "undefined" && data instanceof ArrayBuffer) ||
    ctorName === "ArrayBuffer"
  ) {
    return new TextDecoder().decode(new Uint8Array(data as ArrayBuffer)).trim();
  }
  if (ArrayBuffer.isView(data)) {
    const view = data as ArrayBufferView;
    return new TextDecoder()
      .decode(new Uint8Array(view.buffer, view.byteOffset, view.byteLength))
      .trim();
  }

  const isBlobLike =
    (typeof Blob !== "undefined" && data instanceof Blob) ||
    ctorName === "Blob" ||
    typeof (data as { size?: unknown }).size === "number";
  if (isBlobLike) {
    const anyBlob = data as { text?: () => Promise<string>; arrayBuffer?: () => Promise<ArrayBuffer> };
    if (typeof anyBlob.text === "function") return (await anyBlob.text()).trim();
    if (typeof anyBlob.arrayBuffer === "function") {
      const buf = await anyBlob.arrayBuffer();
      return new TextDecoder().decode(new Uint8Array(buf)).trim();
    }
    // Last-resort for environments (older jsdom) where Blob has no reader methods.
    // Last-resort for environments (older jsdom) where Blob has no reader methods.
    if (typeof FileReader !== "undefined") {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(data as Blob);
      });
      return text.trim();
    }
    if (typeof Response !== "undefined") {
      return (await new Response(data as Blob).text()).trim();
    }
  }

  if (typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (typeof obj.text === "string") return obj.text.trim();
    if (typeof obj.data === "string") return obj.data.trim();
    if (typeof obj.signature === "string") return obj.signature.trim();
    if (typeof obj.certificate === "string") return obj.certificate.trim();
  }

  return String(data).trim();
}
